// src/ClockInOutCard.js - MODIFICATO PER TIMBRATURA FLESSIBILE

import React, { useState, useEffect, useCallback } from 'react'; // Assicurati che useState sia importato
import { db } from './firebase';
import { useUser } from './UserContext';
import { collection, addDoc, Timestamp, query, where, orderBy, limit, onSnapshot, getDoc, doc, GeoPoint, getDocs, updateDoc } from 'firebase/firestore';
import moment from 'moment';
import { toast } from 'react-toastify';
import { logAction } from './loggingService';
import { DiscrepancyModal } from './DiscrepancyModal';
import { ChangeShiftModal } from './Modals';
import { LeaveRequestModal } from './LeaveRequestModal';
import { BarcodeScannerModal } from './BarcodeScannerModal';
import { createNotification } from './notificationService';

// Funzione per calcolare la distanza (formula Haversine)
function getDistanceFromLatLonInM(lat1, lon1, lat2, lon2) {
  const R = 6371e3; // Raggio della Terra in metri
  const dLat = deg2rad(lat2 - lat1);
  const dLon = deg2rad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c; // Distanza in metri
  return distance;
}

function deg2rad(deg) {
  return deg * (Math.PI / 180);
}

const TOLERANCE_MINUTES = 30;

const checkAndClearQRStatus = (userCenterId) => {
    const today = moment().format('YYYY-MM-DD');
    const lastScanDate = localStorage.getItem('lastScanDate');
    const qrId = localStorage.getItem('centerQrId');

    // Se la data non è oggi O l'ID non è presente O l'ID non corrisponde al centro
    if (lastScanDate !== today || !qrId || !qrId.startsWith(userCenterId)) { 
        localStorage.removeItem('lastScanDate');
        localStorage.removeItem('centerQrId');
        return false;
    }
    return true;
};

function ClockInOutCard() {
    const { userProfile } = useUser();
    const [status, setStatus] = useState('loading'); // loading, out, in, on_break
    const [lastLogTime, setLastLogTime] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [canClock, setCanClock] = useState(false);
    const [locationError, setLocationError] = useState('');
    const [geoConfig, setGeoConfig] = useState(null);
    const [isConfigLoading, setIsConfigLoading] = useState(true);
    const [currentLocation, setCurrentLocation] = useState(null);

    const [isQRScannedToday, setIsQRScannedToday] = useState(false);
    const [isScannerOpen, setIsScannerOpen] = useState(false);

    const [isCardActive, setIsCardActive] = useState(false); 

    const [isDiscrepancyModalOpen, setIsDiscrepancyModalOpen] = useState(false);
    const [discrepancyData, setDiscrepancyData] = useState({ difference: 0, dayString: '' });
    const [isOvertimeModalOpen, setIsOvertimeModalOpen] = useState(false);
    const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false);

    // --- NUOVO STATO PER IL SELETTORE ---
    const [selectedClockType, setSelectedClockType] = useState('clock-in'); // Default 'Entrata'
    // --- FINE NUOVO STATO ---

    const shiftPermission = userProfile?.permissions?.shifts || 'none';
    const isShiftManager = shiftPermission === 'completo' || shiftPermission === 'semi-completo' || userProfile?.role === 'Super Admin';

    // Carica configurazione Geo Fence (invariato)
    useEffect(() => {
        const fetchGeoConfig = async () => {
            setIsConfigLoading(true);
            if (!userProfile || !userProfile.centerId) {
                setIsConfigLoading(false);
                setLocationError("Dati utente non caricati.");
                return;
            }
            try {
                const geoFenceDocRef = doc(db, 'centers', userProfile.centerId, 'settings', 'geoFenceConfig');
                const docSnap = await getDoc(geoFenceDocRef);
                if (docSnap.exists() && docSnap.data().latitude && docSnap.data().longitude && docSnap.data().radius > 0) {
                    setGeoConfig(docSnap.data());
                } else {
                    setGeoConfig(null);
                    setLocationError("L'area di timbratura non è configurata.");
                }
            } catch (error) {
                setGeoConfig(null);
                setLocationError("Errore caricamento configurazione area.");
            } finally {
                setIsConfigLoading(false);
            }
        };
        fetchGeoConfig();
    }, [userProfile]);

    // Carica stato Timelog e QR Code (invariato)
    useEffect(() => {
        if (!userProfile || !userProfile.centerId) return;

        // 4a. Verifica lo stato QR (reset a mezzanotte)
        setIsQRScannedToday(checkAndClearQRStatus(userProfile.centerId));
        
        // 4b. Avvia il listener timelog solo se la config è caricata
        if (isConfigLoading) return;
        
        const q = query(
            collection(db, 'centers', userProfile.centerId, 'timelogs'),
            where('userId', '==', userProfile.uid),
            orderBy('timestamp', 'desc'),
            limit(1)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            if (snapshot.empty) {
                setStatus('out');
                setLastLogTime(null);
            } else {
                const lastLog = snapshot.docs[0].data();
                setLastLogTime(lastLog.timestamp.toDate());
                switch (lastLog.type) {
                    case 'clock-in':
                    case 'end-break': setStatus('in'); break;
                    case 'start-break': setStatus('on_break'); break;
                    case 'clock-out': setStatus('out'); break;
                    default: setStatus('out');
                }
            }
        }, (error) => {
             setStatus('error');
        });

        return () => unsubscribe();
    }, [userProfile, isConfigLoading]);


    // Funzione per verificare la posizione (invariato)
    const checkLocation = useCallback(() => {
        setLocationError('');
        setCurrentLocation(null);
        setCanClock(false);
        
        if (isConfigLoading || !geoConfig) { 
             if(!isConfigLoading && !geoConfig) setLocationError("Area timbratura non configurata.");
            return Promise.resolve(false);
        }
        if (!isQRScannedToday) {
             setLocationError("Scansiona il QR Code per confermare la presenza.");
             return Promise.resolve(false);
        }
        
        if (!navigator.geolocation) {
             setLocationError("Geolocalizzazione non supportata.");
            return Promise.resolve(false);
        }
        setIsLoading(true);
        return new Promise((resolve) => {
             const options = { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 };
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const currentLat = position.coords.latitude;
                    const currentLon = position.coords.longitude;
                    setCurrentLocation({ latitude: currentLat, longitude: currentLon });
                    const distance = getDistanceFromLatLonInM(geoConfig.latitude, geoConfig.longitude, currentLat, currentLon);
                    const allowedRadius = geoConfig.radius;
                    
                    if (distance <= allowedRadius) {
                        setCanClock(true);
                        resolve(true);
                    } else {
                        setLocationError(`Sei a ${distance.toFixed(0)} metri. Avvicinati per timbrare (max ${allowedRadius}m).`);
                        resolve(false);
                    }
                     setIsLoading(false);
                },
                (error) => {
                    let errMsg = "Impossibile ottenere la posizione. ";
                    if (error.code === error.PERMISSION_DENIED) errMsg += "Permesso negato.";
                    else if (error.code === error.POSITION_UNAVAILABLE) errMsg += "Informazione non disponibile.";
                    else if (error.code === error.TIMEOUT) errMsg += "Timeout scaduto.";
                    else errMsg += `Errore sconosciuto (${error.message}).`;
                     setLocationError(errMsg + " Assicurati che il GPS sia attivo e i permessi concessi.");
                    resolve(false);
                     setIsLoading(false);
                },
                 options
            );
        });
    }, [geoConfig, isConfigLoading, isQRScannedToday]);


    // Funzione per gestire il risultato della scansione (invariato)
    const handleScanResult = async (barcode) => {
        setIsScannerOpen(false); 
        setIsLoading(true); 
        setLocationError('');
        
        if (geoConfig.qrId !== barcode) {
            setLocationError("Codice QR non valido per questo centro!");
            setIsQRScannedToday(false);
            setIsLoading(false);
            return;
        }

        const today = moment().format('YYYY-MM-DD');
        localStorage.setItem('lastScanDate', today);
        localStorage.setItem('centerQrId', barcode); 
        setIsQRScannedToday(true);

        await checkLocation();
        setIsCardActive(true); 
        setIsLoading(false);
    };

    // Funzione per attivare la Card (invariato)
    const handleActivateCard = async () => {
        if (isConfigLoading || !geoConfig) return; 
        setIsCardActive(true); 

        if (!checkAndClearQRStatus(userProfile.centerId)) {
            setIsScannerOpen(true);
            setLocationError("Scansiona il codice QR del centro.");
        } else {
            setIsLoading(true);
            await checkLocation();
            setIsLoading(false);
        }
    };
    
    // --- MODIFICA handleClock: Rimossa validazione stato ---
    const handleClock = async (type) => {
        // Controllo di sicurezza generale
        if (!userProfile || isLoading || isConfigLoading || !geoConfig || !canClock || !isQRScannedToday) { return; } 

        setIsLoading(true);
        
        try {
            const now = Timestamp.now();
            const clockOutOrInTime = now.toDate();
            const shiftDate = moment(clockOutOrInTime).format('YYYY-MM-DD');

            // 1. Salva la timbratura su Firestore
            await addDoc(collection(db, 'centers', userProfile.centerId, 'timelogs'), {
                userId: userProfile.uid,
                userName: userProfile.name,
                timestamp: now,
                type: type,
                shiftDate: shiftDate,
                location: new GeoPoint(currentLocation.latitude, currentLocation.longitude),
                centerId: userProfile.centerId
            });
            
            // 2. Se è un'USCITA, controlla la durata lavorata
            if (type === 'clock-out') {
                console.log("Timbratura uscita registrata. Avvio controllo discrepanza...");
                
                // Recupera tutte le timbrature di OGGI (inclusa quella appena fatta) per fare il calcolo preciso
                const startOfDay = moment(clockOutOrInTime).startOf('day').toDate();
                const endOfDay = moment(clockOutOrInTime).endOf('day').toDate();
                
                const qLogs = query(
                    collection(db, 'centers', userProfile.centerId, 'timelogs'),
                    where('userId', '==', userProfile.uid),
                    where('timestamp', '>=', startOfDay),
                    where('timestamp', '<=', endOfDay),
                    orderBy('timestamp', 'asc')
                );
                
                const snapshot = await getDocs(qLogs);
                const todaysLogs = snapshot.docs.map(doc => doc.data());

                // Chiama la funzione di controllo (che aprirà il modale se diff >= 30 min)
                await checkWorkDurationAndShowPopup(userProfile, clockOutOrInTime, todaysLogs);
            } else {
                // Se è entrata o pausa, finiamo qui
                setIsLoading(false);
            }

        } catch (error) {
            console.error("Errore handleClock:", error);
            setLocationError("Errore durante il salvataggio o controllo.");
            setIsLoading(false); 
        }
    };
    // --- FINE MODIFICA handleClock ---
    
    const calculateExpectedDuration = async (user, dayMoment) => {
        if (!user || !user.includeInShifts || !user.timeSlots) {
            return { expectedMinutes: 0, shiftDetailsFound: false };
        }
        const dayString = dayMoment.format('YYYY-MM-DD');
        const monthString = dayMoment.format('YYYY-MM');
        let expectedMinutes = 0;
        let shiftDetailsFound = false;
        try {
            const scheduleDocRef = doc(db, 'centers', userProfile.centerId, 'schedules', monthString);
            const scheduleSnap = await getDoc(scheduleDocRef);
            const scheduleData = scheduleSnap.exists() ? JSON.parse(scheduleSnap.data().schedule || '{}') : {};
            const plannedShift = scheduleData[dayString]?.[user.uid] || scheduleData[dayString]?.[user.name];
            if (plannedShift && plannedShift.name !== 'Ferie' && plannedShift.name !== 'Malattia' && plannedShift.name !== 'CHIUSO') {
                let baseMinutes = 0;
                let pauseMinutes = 0;
                shiftDetailsFound = true;
                if (plannedShift.name === 'Nuovo Orario' && plannedShift.customTime) {
                    const start = moment(plannedShift.customTime.start, 'HH:mm');
                    const end = moment(plannedShift.customTime.end, 'HH:mm');
                    if (start.isValid() && end.isValid() && end.isAfter(start)) {
                        baseMinutes = end.diff(start, 'minutes');
                    }
                } else {
                    let shiftNameForLookup = plannedShift.originalName || plannedShift.name;
                    if(plannedShift.isRecovery && !shiftNameForLookup.endsWith(' REC')) {
                        shiftNameForLookup += ' REC';
                    }
                    const dayString = dayMoment.format('YYYY-MM-DD');
const timeSlot = user.timeSlots.find(ts => {
    const from = ts.validFrom || "2020-01-01";
    const until = ts.validUntil || "2100-01-01";

    return ts.name === shiftNameForLookup && 
           moment(dayString).isBetween(from, until, 'day', '[]');
});
                    if (timeSlot) {
                        const start = moment(timeSlot.startTime, 'HH:mm');
                        const end = moment(timeSlot.endTime, 'HH:mm');
                        if (start.isValid() && end.isValid() && end.isAfter(start)) {
                            baseMinutes = end.diff(start, 'minutes');
                            pauseMinutes = timeSlot.pauseMinutes || 0;
                        }
                    } else {
                         console.warn(`Timeslot non trovata per ${user.name} - turno ${shiftNameForLookup} il ${dayString}`);
                         shiftDetailsFound = false;
                    }
                }
                expectedMinutes = baseMinutes - pauseMinutes;
                if (plannedShift.overtime && (plannedShift.overtime.h > 0 || plannedShift.overtime.m > 0)) {
                    expectedMinutes += (plannedShift.overtime.h * 60) + plannedShift.overtime.m;
                }
                if (plannedShift.leave && (plannedShift.leave.h > 0 || plannedShift.leave.m > 0)) {
                    if (plannedShift.name !== 'Permesso') {
                         expectedMinutes -= (plannedShift.leave.h * 60) + plannedShift.leave.m;
                    } else {
                        expectedMinutes = 0;
                    }
                }
                expectedMinutes = Math.max(0, expectedMinutes);
            }
        } catch (error) {
            console.error("Errore calcolo durata prevista:", error);
            shiftDetailsFound = false;
        }
        console.log(`Durata Prevista per ${user.name} il ${dayString}: ${expectedMinutes} min. Turno trovato: ${shiftDetailsFound}`);
        return { expectedMinutes, shiftDetailsFound };
    };
    const calculateActualDuration = (dayLogs) => {
        let totalWorkMinutes = 0;
        let totalBreakMinutes = 0;
        let lastClockIn = null;
        let lastBreakStart = null;
        const sortedLogs = [...dayLogs].sort((a, b) => a.timestamp.toDate() - b.timestamp.toDate());
        for (const log of sortedLogs) {
            const logTime = moment(log.timestamp.toDate());
            switch (log.type) {
                case 'clock-in':
                    if (lastClockIn === null) {
                        lastClockIn = logTime;
                    } else {
                         console.warn("Trovato clock-in senza clock-out precedente:", log);
                    }
                    if (lastBreakStart !== null) {
                         console.warn("Trovato clock-in durante una pausa:", log);
                         totalBreakMinutes += logTime.diff(lastBreakStart, 'minutes');
                         lastBreakStart = null;
                    }
                    break;
                case 'clock-out':
                    if (lastClockIn !== null) {
                        if (lastBreakStart !== null) {
                             console.warn("Trovato clock-out durante una pausa:", log);
                             totalBreakMinutes += logTime.diff(lastBreakStart, 'minutes');
                             lastBreakStart = null;
                        }
                        totalWorkMinutes += logTime.diff(lastClockIn, 'minutes');
                        lastClockIn = null; 
                    } else {
                         console.warn("Trovato clock-out senza clock-in precedente:", log);
                    }
                    break;
                case 'start-break':
                    if (lastClockIn !== null && lastBreakStart === null) {
                        lastBreakStart = logTime;
                    } else {
                         console.warn("Trovato start-break in stato non valido:", log);
                    }
                    break;
                case 'end-break':
                    if (lastBreakStart !== null) {
                        totalBreakMinutes += logTime.diff(lastBreakStart, 'minutes');
                        lastBreakStart = null; 
                    } else {
                         console.warn("Trovato end-break senza start-break precedente:", log);
                    }
                    break;
                default:
                    break;
            }
        }
        const actualNetMinutes = Math.max(0, totalWorkMinutes - totalBreakMinutes);
         console.log(`Durata Effettiva Calcolata: ${actualNetMinutes} min (Lordo: ${totalWorkMinutes}, Pausa: ${totalBreakMinutes})`);
        return actualNetMinutes;
    };
     const checkWorkDurationAndShowPopup = async (user, clockOutTime, dayLogs) => {
        if (!user || !user.includeInShifts) {
            console.log("Controllo durata saltato: utente non incluso nei turni.");
            setIsLoading(false);
            return;
        }
        const dayMoment = moment(clockOutTime);
        const dayString = dayMoment.format('YYYY-MM-DD');
        const { expectedMinutes, shiftDetailsFound } = await calculateExpectedDuration(user, dayMoment);
        if (!shiftDetailsFound) {
             console.log("Controllo durata saltato: nessun turno/timeslot trovato.");
             setIsLoading(false);
             return;
        }
        const hasClockIn = dayLogs.some(log => log.type === 'clock-in');
        const hasClockOut = dayLogs.some(log => log.type === 'clock-out');
        if (!hasClockIn || !hasClockOut) {
             console.log("Controllo durata saltato: timbrature incomplete (manca entrata?).");
             setIsLoading(false);
             return;
        }
        const actualMinutes = calculateActualDuration(dayLogs);
        const difference = actualMinutes - expectedMinutes;
        console.log(`Confronto Durata: Effettiva=${actualMinutes}min, Prevista=${expectedMinutes}min, Diff=${difference}min`);
        if (Math.abs(difference) > TOLERANCE_MINUTES) {
            setDiscrepancyData({ difference: difference, dayString: dayString });
            setIsDiscrepancyModalOpen(true);
        } else {
            console.log("Nessuna discrepanza significativa rilevata.");
            setIsLoading(false);
        }
     };
     const handleDiscrepancyIgnore = () => {
         setIsDiscrepancyModalOpen(false);
         setIsLoading(false);
     };
     const handleDiscrepancyAddOvertime = () => {
         setIsDiscrepancyModalOpen(false);
         setIsOvertimeModalOpen(true);
     };
     const handleDiscrepancyRequestLeave = () => {
         setIsDiscrepancyModalOpen(false);
         setIsLeaveModalOpen(true);
     };
     const handleSaveOvertime = async (shiftUpdateData) => {
          // Nota: 'person' qui è il nome, ma noi abbiamo bisogno dell'ID per sicurezza
          const { dayStr, overtime } = shiftUpdateData; 
          const monthString = moment(dayStr, 'YYYY-MM-DD').format('YYYY-MM');
          const scheduleDocRef = doc(db, 'centers', userProfile.centerId, 'schedules', monthString);
          
          try {
             const scheduleSnap = await getDoc(scheduleDocRef);
             let scheduleData = {};
             
             if (scheduleSnap.exists()) {
                 scheduleData = JSON.parse(scheduleSnap.data().schedule || '{}');
             }
             
             if (!scheduleData[dayStr]) scheduleData[dayStr] = {};
             
             // --- CORREZIONE LOGICA CHIAVE UTENTE ---
             // Cerchiamo qual è la chiave corretta per questo utente in questo giorno.
             // Priorità: 1. ID Utente (UID) -> 2. Nome Utente (Legacy)
             let userKey = userProfile.uid;

             if (scheduleData[dayStr][userProfile.uid]) {
                 userKey = userProfile.uid; // Trovato per ID
             } else if (scheduleData[dayStr][userProfile.name]) {
                 userKey = userProfile.name; // Trovato per Nome (vecchia gestione)
             } else {
                 // Se non c'è nulla, usiamo l'ID che è lo standard attuale
                 userKey = userProfile.uid;
             }

             // Recupera il turno esistente o creane uno vuoto se non esiste
             const existingShift = scheduleData[dayStr][userKey] || { name: 'Presente', source: 'Timbratura' };

             // Aggiorna lo straordinario mantenendo gli altri dati del turno
             scheduleData[dayStr][userKey] = {
                 ...existingShift, // Mantiene nome turno, orari, ecc.
                 overtime: overtime
             };
             
             await updateDoc(scheduleDocRef, { schedule: JSON.stringify(scheduleData) });
             
             // Log dell'azione
             await logAction(userProfile, `ha registrato ${overtime.h}h ${overtime.m}m di straordinario il ${moment(dayStr).format('DD/MM')}`);

             // --- Notifica ai Manager ---
             const staffRef = collection(db, 'centers', userProfile.centerId, 'staff');
             const qManager = query(staffRef, where("permissions.shifts", "in", ["completo", "semi-completo"]));
             const managersSnap = await getDocs(qManager);
             
             managersSnap.forEach(mgr => {
                 if (mgr.id !== userProfile.uid) {
                     createNotification(
                        mgr.id,
                        userProfile,
                        `ha registrato ${overtime.h}h ${overtime.m}m di straordinario per il ${moment(dayStr).format('DD/MM')}.`,
                        '/turni'
                     );
                 }
             });

             toast.success("Straordinario registrato correttamente.");
             setIsOvertimeModalOpen(false);
          } catch (error) {
               console.error("Errore salvataggio straordinario:", error);
               toast.error("Errore durante la registrazione dello straordinario.");
          }
     };
      const handleLeaveRequestSubmitted = () => {
          setIsLeaveModalOpen(false);
          setIsLoading(false);
     };

    // --- NUOVA FUNZIONE renderButtons ---
    // Determina quale pulsante mostrare
    const renderButtons = () => {
        if (isConfigLoading || !geoConfig) return null; // Non mostrare nulla se non configurato
        
        // Se QR scansionato e GPS OK, mostra la NUOVA UI (selettore + pulsante)
        if (isQRScannedToday && canClock) {
            const buttonDisabled = isLoading;
            
            return (
                <div className="clock-actions-flexible"> {/* Nuova classe per lo stile */}
                    {/* Selettore a tendina */}
                    <div className="form-group" style={{marginBottom: '15px'}}>
                        <label htmlFor="clock-type-select" style={{color: '#333', fontWeight: '500', marginBottom: '5px'}}>Seleziona Azione</label>
                        <select 
                            id="clock-type-select"
                            value={selectedClockType}
                            onChange={(e) => setSelectedClockType(e.target.value)}
                            // Aggiungo stili inline per farlo assomigliare agli altri input
                            style={{
                                width: '100%',
                                padding: '10px',
                                fontSize: '1rem',
                                borderRadius: '5px',
                                border: '1px solid #ccc',
                                boxSizing: 'border-box',
                                appearance: 'none', // Stile più pulito
                                backgroundImage: 'url("data:image/svg+xml;utf8,<svg fill=\'%23333\' height=\'24\' viewBox=\'0 0 24 24\' width=\'24\' xmlns=\'http://www.w3.org/2000/svg\'><path d=\'M7 10l5 5 5-5z\'/><path d=\'M0 0h24v24H0z\' fill=\'none\'/></svg>")',
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: 'right 10px center',
                                paddingRight: '30px'
                            }}
                        >
                            <option value="clock-in">✅ Entrata</option>
                            <option value="start-break">☕ Inizio Pausa</option>
                            <option value="end-break">▶️ Fine Pausa</option>
                            <option value="clock-out">❌ Uscita</option>
                        </select>
                    </div>

                    {/* Pulsante Unico "Timbra" */}
                    <button 
                        onClick={() => handleClock(selectedClockType)} // Passa il tipo selezionato
                        disabled={buttonDisabled} 
                        className="clock-button clock-in-btn" // Usiamo uno stile verde
                        style={{width: '100%'}} // Larghezza piena
                    >
                        Timbra
                    </button>
                </div>
            );
        }

        // Altrimenti, non mostriamo pulsanti (il footer gestisce le azioni)
        return null;
    };
    // --- FINE NUOVA FUNZIONE renderButtons ---

    // renderFooterButtons (invariato)
    const renderFooterButtons = () => {
        if (!geoConfig) return null;
        if (!isQRScannedToday) {
            return (
                 <button
                    onClick={(e) => { e.stopPropagation(); setIsScannerOpen(true); }}
                    disabled={isLoading || isConfigLoading}
                    className="clock-refresh-btn"
                    style={{ backgroundColor: '#0d6efd', color: 'white', border: 'none' }}
                >
                    <span role="img" aria-label="QR Code">📷</span> Scansiona QR Code
                </button>
            );
        }
        return (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', width: '100%' }}>
                <button
                    onClick={(e) => { e.stopPropagation(); checkLocation(); }}
                    disabled={isLoading || isConfigLoading}
                    className="clock-refresh-btn"
                    title="Verifica posizione attuale"
                >
                    🔄 Verifica Posizione
                </button>
                 <button
                    onClick={(e) => { e.stopPropagation(); setIsScannerOpen(true); }}
                    disabled={isLoading || isConfigLoading}
                    className="clock-refresh-btn"
                    title="Forza Nuova Scansione QR"
                >
                    <span role="img" aria-label="QR Code">📷</span> Nuova Scansione QR
                </button>
            </div>
        );
    };

    // getStatusText (invariato)
    const getStatusText = () => {
        if (!isQRScannedToday) return <span className="clock-status clock-status-out">Inattivo (Scansione richiesta)</span>;
        switch (status) {
            case 'in': return <span className="clock-status clock-status-in">In Servizio</span>;
            case 'out': return <span className="clock-status clock-status-out">Fuori Servizio</span>;
            case 'on_break': return <span className="clock-status clock-status-break">In Pausa</span>;
            case 'loading': return <span className="clock-status clock-status-loading">Verifica...</span>;
            case 'error': return <span className="clock-status clock-status-error">Errore</span>;
            default: return '';
        }
    };

   return (
        <> 
            {/* ... (tutti i modali rimangono invariati) ... */}
            {isDiscrepancyModalOpen && (
                <DiscrepancyModal
                    isOpen={isDiscrepancyModalOpen}
                    onClose={handleDiscrepancyIgnore}
                    timeDifferenceMinutes={discrepancyData.difference}
                    isManager={isShiftManager}
                    onAddOvertime={handleDiscrepancyAddOvertime}
                    onRequestLeave={handleDiscrepancyRequestLeave}
                />
            )}
            {isOvertimeModalOpen && (
                 <ChangeShiftModal
                     modalData={{
                         person: userProfile.name,
                         dayStr: discrepancyData.dayString,
                         shiftData: {
                             name: 'Straordinario',
                             overtime: {
                                h: Math.floor(discrepancyData.difference / 60),
                                m: discrepancyData.difference % 60
                             }
                         }
                     }}
                     onConfirm={handleSaveOvertime}
                     onClose={() => setIsOvertimeModalOpen(false)}
                 />
            )}
            {isLeaveModalOpen && (
                 <LeaveRequestModal
                     onClose={() => setIsLeaveModalOpen(false)}
                     onSubmit={handleLeaveRequestSubmitted}
                     personId={userProfile.uid}
                     personName={userProfile.name}
                     personProfileData={userProfile}
                     selectedDate={discrepancyData.dayString}
                     leaveType="Permesso"
                     leaveHours={{
                         h: Math.floor(Math.abs(discrepancyData.difference) / 60),
                         m: Math.abs(discrepancyData.difference) % 60
                     }}
                 />
             )}
            {isScannerOpen && <BarcodeScannerModal onScan={handleScanResult} onClose={() => { setIsScannerOpen(false); setIsLoading(false); }} />}

            {/* Il JSX della Card rimane quasi identico, ma la funzione renderButtons() ora mostrerà la nuova UI */}
            <div 
                className={`dashboard-card clock-card ${!isCardActive && !isConfigLoading && geoConfig ? 'clickable-card' : ''}`}
                onClick={!isCardActive ? handleActivateCard : undefined}
                style={!isCardActive && !isConfigLoading && geoConfig ? { cursor: 'pointer' } : {}}
            >
                <div className="clock-card-header">
                     <h3>📊 Timbra Presenze</h3>
                     {(isCardActive || isConfigLoading || !geoConfig) && (
                         <div className="clock-status-display">
                             {getStatusText()}
                             {lastLogTime && (
                                <span className="clock-last-log">
                                    (Ultima: {moment(lastLogTime).format('HH:mm:ss')})
                                </span>
                            )}
                         </div>
                     )}
                </div>

                <div className="clock-card-body">
                    {!isCardActive ? (
                        <>
                            {isConfigLoading && <p className="clock-loading-text">Caricamento configurazione...</p>}
                            {!isConfigLoading && !geoConfig && <p className="clock-error-text">⚠️ Area timbratura non configurata.</p>}
                            {!isConfigLoading && geoConfig && (
                                <p className="info-text" style={{color: '#0d6efd', fontWeight: '500', fontSize: '1rem', padding: '20px 0'}}>
                                    Clicca qui per {(checkAndClearQRStatus(userProfile.centerId) && !isLoading) ? 'verificare la posizione' : 'scansionare il QR code'} e attivare la timbratura.
                                </p>
                            )}
                        </>
                    ) : (
                        <>
                            {isLoading && <p className="clock-loading-text">Verifica in corso...</p>}
                            {!isLoading && isQRScannedToday && locationError && (
                                <p className="clock-error-text">⚠️ {locationError}</p>
                            )}
                            {!isLoading && isQRScannedToday && !locationError && !canClock && geoConfig && (
                                <p className="clock-warning-text">
                                    Avvicinati all'area consentita (entro {geoConfig.radius}m) per timbrare.
                                </p>
                            )}
                            {/* I pulsanti (ora selettore + timbra) verranno renderizzati qui */}
                            <div className="clock-actions">
                                {renderButtons()}
                            </div>
                        </>
                    )}
                </div>

                <div className="clock-card-footer">
                    {isCardActive && renderFooterButtons()}
                </div>
            </div>
        </>
    );
}

export default ClockInOutCard;